import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { BOOKS, WISDOM } from "@/lib/content";
import { RefreshCw } from "lucide-react";

type Quote = {
  text: string;
  sourceTitle: string;
  href: string;
};

/** Draw quotable phrases from the real KAfrequency library. */
function collectQuotes(): Quote[] {
  const out: Quote[] = [];
  for (const w of WISDOM) {
    if (w.pullQuote) out.push({ text: w.pullQuote, sourceTitle: w.title, href: `/wisdom/${w.slug}` });
    if (w.excerpt && w.excerpt.length <= 220) {
      out.push({ text: w.excerpt, sourceTitle: w.title, href: `/wisdom/${w.slug}` });
    }
  }
  for (const b of BOOKS) {
    if (b.subtitle) out.push({ text: b.subtitle, sourceTitle: b.title, href: `/books/${b.slug}` });
  }
  return out;
}

export function LivingQuote() {
  const [pool] = useState<Quote[]>(() => collectQuotes());
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!pool.length) return;
    setIndex(Math.floor(Math.random() * pool.length));
  }, [pool.length]);

  if (!pool.length) return null;
  const q = pool[index];

  return (
    <aside
      aria-label="Living quote from the KAfrequency library"
      className="relative overflow-hidden rounded-2xl border hairline bg-charcoal/40 p-8 backdrop-blur-sm"
      data-reveal
    >
      <div className="absolute inset-0 halo opacity-40" aria-hidden />
      <div className="relative">
        <p className="font-serif text-2xl leading-snug text-ivory sm:text-3xl">
          <span aria-hidden className="text-gold-luminous">&ldquo;</span>
          {q.text}
          <span aria-hidden className="text-gold-luminous">&rdquo;</span>
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <Link
            to={q.href}
            className="text-xs uppercase tracking-[0.3em] text-gold hover:text-gold-luminous"
          >
            — {q.sourceTitle}
          </Link>
          <button
            type="button"
            onClick={() => {
              if (pool.length < 2) return;
              let next = index;
              while (next === index) next = Math.floor(Math.random() * pool.length);
              setIndex(next);
            }}
            aria-label="Draw another quote"
            className="hover-glow inline-flex items-center gap-2 rounded-full border hairline px-3 py-1.5 text-xs text-muted-foreground hover:border-gold hover:text-gold"
          >
            <RefreshCw size={12} aria-hidden />
            Another
          </button>
        </div>
      </div>
    </aside>
  );
}
